import {
  Hospital,
  LayoutDashboard,
  ReceiptText,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  type IconComponent,
} from "Components/ui/icons";

export type PermissionAction = "read" | "write" | "delete" | "configure" | "approve";

export interface NavItem {
  label: string;
  path: string;
  icon: IconComponent;
  /** Hidden from navigation unless the user has this permission. */
  permission?: { resource: string; action: PermissionAction };
  /** Other URL prefixes that belong to this item (detail pages). */
  matches?: string[];
  /** Extra words the command palette should match. */
  keywords?: string[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** The app's navigation: the sidebar, breadcrumbs and command palette all read from here. */
export const NAVIGATION: NavGroup[] = [
  {
    label: "Overview",
    items: [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: LayoutDashboard,
        keywords: ["home", "health"],
      },
    ],
  },
  {
    label: "Clients",
    items: [
      {
        label: "Tenants",
        path: "/clients",
        icon: Hospital,
        permission: { resource: "tenantManagement", action: "read" },
        matches: ["/module/"],
        keywords: ["clients", "hospitals", "deployments", "modules", "metahos"],
      },
      {
        label: "Billing",
        path: "/billing",
        icon: ReceiptText,
        permission: { resource: "billing", action: "read" },
        keywords: ["invoices", "payments", "plans", "subscriptions"],
      },
    ],
  },
  {
    label: "Access",
    items: [
      {
        label: "Users",
        path: "/hr/users",
        icon: Users,
        permission: { resource: "userManagement", action: "read" },
        keywords: ["admins", "accounts"],
      },
      {
        label: "Roles",
        path: "/hr/roles",
        icon: ShieldCheck,
        permission: { resource: "roleManagement", action: "read" },
        keywords: ["permissions", "rbac"],
      },
    ],
  },
  {
    label: "Governance",
    items: [
      {
        label: "Audit logs",
        path: "/audit-logs",
        icon: ScrollText,
        permission: { resource: "auditLogs", action: "read" },
        keywords: ["history", "activity", "changes"],
      },
    ],
  },
  {
    label: "System",
    items: [
      {
        label: "Settings",
        path: "/settings",
        icon: Settings,
        keywords: ["configuration", "maintenance"],
      },
    ],
  },
];

export const isNavItemActive = (item: NavItem, pathname: string): boolean =>
  [item.path, ...(item.matches || [])].some(
    (prefix) =>
      pathname === prefix || pathname.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`)
  );

/** Group and item for the current URL, for breadcrumbs. */
export const findNavLocation = (pathname: string): { group: NavGroup; item: NavItem } | null => {
  for (const group of NAVIGATION) {
    const item = group.items.find((candidate) => isNavItemActive(candidate, pathname));
    if (item) {
      return { group, item };
    }
  }
  return null;
};
